import { useCallback, useMemo } from 'react'

import { isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import {
  createInternships,
  updateInternship,
  deleteInternships,
  SerializationContexts,
  resetToPending,
  acceptInternship,
  refuseInternship,
  preAccept as preAcceptInternship,
  validateInternship,
  invalidateInternship,
  duplicateInternships,
  resetToCreated,
  updateBulkIsDraft,
  updateBulkIsDraftSchedules,
  deleteInternshipsShifts
} from '../internship'
import { onError, onSuccess } from '../../apiHelper'
import { Internship } from '../../entities/internship'
import { Actions, toItemActionPairs } from '../../events'
import { getIdOrNull, isNonEmptyArray } from '../..'
import { HttpStatuses, SerializationHeaders } from '../../http'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'

const HEADERS = [new SerializationHeaders(SerializationContexts.INTERNSHIP)]

const useInternship = ({ callback } = {}) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const safeCallback = useMemo(() => isFunction(callback) ? callback : () => { }, [callback])

  const create = useCallback(internships => {
    if (!isNonEmptyArray(internships)) {
      internships = [internships]
    }

    internships = internships.map(Internship.toDTO)

    return createInternships(user, internships, HEADERS).then(({ data }) => {
      safeCallback(...toItemActionPairs(data, Actions.CREATE))

      onSuccess(t('internship.create.success'))
    }).catch(error => {
      console.error(error)
      onError(t('internship.create.error'))
    })
  }, [user, t, safeCallback])

  const upsert = useCallback((internship, previousState, deleteOnUpdate = false) => {
    const config = internship.id === -1 ? { request: createInternships, action: Actions.CREATE } : { request: updateInternship, action: Actions.UPDATE }

    safeCallback(internship, config.action)

    return config.request(user, Internship.toDTO(internship), HEADERS).then(({ data }) => {
      const newInternship = new Internship(data)

      if (config.action === Actions.CREATE) {
        safeCallback(internship, Actions.DELETE, newInternship, Actions.CREATE)
      } else if (deleteOnUpdate) {
        safeCallback(previousState, Actions.DELETE, newInternship, Actions.UPDATE)
      } else {
        safeCallback(newInternship, Actions.UPDATE)
      }
      onSuccess(t(`internship.${config.action.toLowerCase()}.success`))
    }).catch(reason => {
      console.error(reason)

      config.action === Actions.CREATE ? safeCallback(internship, Actions.DELETE) : safeCallback(previousState, Actions.UPDATE)
      onError(t(`internship.${config.action.toLowerCase()}.error`))
    })
  }, [t, user, safeCallback])

  const del = useCallback(internships => {
    const isBulk = isNonEmptyArray(internships)

    if (!isBulk) {
      internships = [internships]
    }

    internships = internships.map(getIdOrNull)

    return deleteInternships(user, internships).then(({ data }) => {
      safeCallback(...toItemActionPairs(data, Actions.DELETE))

      onSuccess(t('internship.delete.success'))

      return data
    }).catch(error => {
      console.error(error)
      onError(t('internship.delete.error'))
    })
  }, [t, user, safeCallback])

  const setAsPending = useCallback(internship => {
    return resetToPending(user, internship).then(({ data }) => {
      safeCallback(...toItemActionPairs({ ...internship, state: data.state }, Actions.UPDATE))

      onSuccess(t('internship.set_as_pending.success'))
    }).catch(error => {
      console.error(error)
      onError(t('internship.set_as_pending.error'))
    })
  }, [t, user, safeCallback])

  const setAsCreated = useCallback(internship => {
    return resetToCreated(user, internship).then(({ data }) => {
      safeCallback(...toItemActionPairs({ ...internship, state: data.state }, Actions.UPDATE))

      onSuccess(t('internship.set_as_created.success'))
    }).catch(error => {
      console.error(error)
      onError(t('internship.set_as_created.error'))
    })
  }, [t, user, safeCallback])

  const accept = useCallback(internship => {
    return acceptInternship(user, internship).then(({ data }) => {
      safeCallback(...toItemActionPairs({ ...internship, state: data.state }, Actions.UPDATE))

      onSuccess(t('internship.accept.success'))
    }).catch(error => {
      console.error(error)
      onError(t('internship.accept.error'))
    })
  }, [t, user, safeCallback])

  const refuse = useCallback((internship, reasons, comment) => {
    return refuseInternship(user, internship, reasons, comment).then(({ data }) => {
      safeCallback(...toItemActionPairs({ ...internship, state: data.state }, Actions.UPDATE))

      onSuccess(t('internship.refuse.success'))
    }).catch(error => {
      console.error(error)
      onError(t('internship.refuse.error'))
    })
  }, [t, user, safeCallback])

  const preAccept = useCallback(internship => {
    return preAcceptInternship(user, internship, true).then(({ data }) => {
      safeCallback(...toItemActionPairs({ ...internship, state: data.state }, Actions.UPDATE))

      onSuccess(t('internship.pre_accept.success'))
    }).catch(error => {
      console.error(error)
      onError(t('internship.pre_accept.error'))
    })
  }, [t, user, safeCallback])

  const validate = useCallback(internship => {
    return validateInternship(user, internship).then(({ data, firstInternshipTag }) => {
      safeCallback(...toItemActionPairs({ ...internship, state: data.state }, Actions.UPDATE))

      onSuccess(t('internship.validate.success'))

      return { data, firstInternshipTag }
    }).catch(error => {
      console.error(error)
      onError(t('internship.validate.error'))
    })
  }, [t, user, safeCallback])

  const invalidate = useCallback(internship => {
    return invalidateInternship(user, internship).then(({ data }) => {
      safeCallback(...toItemActionPairs({ ...internship, state: data[0].state }, Actions.UPDATE))

      onSuccess(t('internship.invalidate.success'))

      return { data }
    }).catch(error => {
      console.error(error)

      if (error.status === HttpStatuses.BAD_REQUEST) {
        onError(t('iternship.invalidate.error.missing_data'))
      } else {
        onError(t('internship.invalidate.error'))
      }

      throw error
    })
  }, [t, user, safeCallback])

  const duplicate = useCallback((internships, offset) => {
    const internshipIds = internships.map(internship => internship.id)

    return duplicateInternships(user, internshipIds, offset).then(({ data }) => {
      safeCallback(...toItemActionPairs(data, Actions.CREATE))

      onSuccess(t('internship.duplicate.success'))

      return { data }
    }).catch(error => {
      console.error(error)

      if (error.status === HttpStatuses.BAD_REQUEST) {
        onError(t('internship.duplicate.error.no_internship'))
      } else {
        onError(t('internship.duplicate.error'))
      }

      throw error
    })
  }, [t, user, safeCallback])

  const bulkDraft = useCallback((internships, isDraft) => {
    return updateBulkIsDraft(user, internships.map(i => i.id), isDraft).then(({ data }) => {
      if (data) {
        safeCallback(...toItemActionPairs(data, Actions.UPDATE))
      }

      onSuccess(t(isDraft ? 'internship.bulk_is_draft.success' : 'internship.bulk_is_not_draft.success'))

      return data
    }).catch(() => {
      onError(t(isDraft ? 'internship.bulk_is_draft.error' : 'internship.bulk_is_not_draft.error'))
    })
  }, [t, user, safeCallback])

  const bulkDraftSchedules = useCallback((internships, isDraft) => {
    return updateBulkIsDraftSchedules(user, internships.map(i => i.id), isDraft).then(({ data }) => {
      if (data) {
        safeCallback(...toItemActionPairs(data, Actions.UPDATE))
      }

      onSuccess(t(isDraft ? 'internship.bulk_is_draft_schedules.success' : 'internship.bulk_is_not_draft_schedules.success'))

      return data
    }).catch(() => {
      onError(t(isDraft ? 'internship.bulk_is_draft_schedules.error' : 'internship.bulk_is_not_draft_schedules.error'))
    })
  }, [t, user, safeCallback])

  const draftSchedules = useCallback((internship, isDraft) => {
    return updateBulkIsDraftSchedules(user, [internship.id], isDraft).then(({ data }) => {
      if (data) {
        safeCallback(...toItemActionPairs(data, Actions.UPDATE))
      }

      onSuccess(t(isDraft ? 'internship.draft_schedules.success' : 'internship.undraft_schedules.success'))

      return data
    }).catch(() => {
      onError(t(isDraft ? 'internship.draft_schedules.error' : 'internship.undraft_schedules.error'))
    })
  }, [t, user, safeCallback])

  const deleteShifts = useCallback((internships) => {
    return deleteInternshipsShifts(user, internships.map(i => i.id)).then(({ data }) => {
      if (data) {
        safeCallback(...toItemActionPairs(data, Actions.UPDATE))
      }

      onSuccess(t('internship.delete_schedules.success'))

      return data
    }).catch(() => {
      onError(t('internship.delete_schedules.error'))
    })
  }, [])

  return {
    accept,
    bulkDraft,
    bulkDraftSchedules,
    create,
    delete: del,
    deleteShifts,
    draftSchedules,
    invalidate,
    preAccept,
    refuse,
    setAsPending,
    setAsCreated,
    upsert,
    validate,
    duplicate
  }
}

export default useInternship
